// Plan madde 10.2 — permission check hook.
//
// Returns boolean for "can the current operator perform this action?".
// Consumes useOperatorMe (cached for 5 min by default) and the
// operator-permissions action→roles map; pure compute on the cached
// data (no extra fetch).
//
// Loading semantics: while useOperatorMe is loading (hasn't returned
// the first response yet), usePermission returns FALSE. This is
// deliberate — better to render a disabled button briefly than to
// flash an enabled button that turns out not to be allowed. Callers
// that need to distinguish "loading" from "denied" should read
// useOperatorMe directly.
//
// Error semantics: if useOperatorMe.error is set, usePermission
// returns FALSE. The caller's toast adapter (showErrorToast in the
// onError callback) surfaces the actual error to the user; the
// permission check defaults closed.

import { isPermitted, type OperatorAction } from "@/lib/operator-permissions";

import { useOperatorMe } from "./useOperatorMe";

/**
 * Returns `true` iff the current operator's role list contains at
 * least one role allowed for the action. Defaults closed on
 * loading / error / unknown action.
 *
 * Usage:
 *   const canRefund = usePermission("billing.refund");
 *   <Button disabled={!canRefund}>Issue refund</Button>
 *
 * For route-level gating use <RequireRole> instead — it handles the
 * loading state explicitly with a fallback render.
 */
export function usePermission(action: OperatorAction): boolean {
  const me = useOperatorMe();
  if (me.data === undefined) return false;
  return isPermitted(action, me.data.roles);
}
